import React, { ReactNode } from 'react';
import { getDefaultChainColor, getDefaultLigandColor, getDefaultPocketColor } from '../../contexts/ControlTypes';
import { render_chain_ids } from '../../helpers/TableHelper';
import {
   defaultLigandRepresentation,
   defaultPocketRepresentation,
   defaultRestRepresentation,
   Representation,
   StructureComponentType,
} from '../molstar/Controls/StructureRepresentationComponent';
import { AhojTableBase } from './AhojTableBase';
import ColorPairButton from './ColorPairButton';
import StructureRepresentationControlComponent from './StructureRepresentationControlComponent';

/**
 * Table showing **holo** (ligand-bound) structure matches for the query binding
 * site. Each row represents one PDB structure + chain combination. Extends
 * `AhojTableBase` to inherit:
 * - per-row Molstar toggle, focus, color-pair, and transparency controls
 * - sortable columns via `handleSort` + `getSortedData`
 * - expandable column set (10 columns → 16 columns when `expandedColumns` is true)
 *
 * Ligand controls are present in the per-row edit panel because holo structures
 * always carry a ligand component.
 */
class HoloSitesTable extends AhojTableBase {
   render(): ReactNode {
      const sortedData = this.getSortedData(this.props.data.found_holo);
      const exp = this.expandedColumns;
      // DEFAULT: 10 cols — Structure | AT | Lig | pRMSD | SASA | 👁 | ✏ | Seq | ℹ | 🎨
      // EXPANDED: 16 cols — + Res | MBR | Dis | RMSD | TM | Vol
      const colSpan = exp ? 16 : 10;
      return (
         <div className="found-struct-box">
            {this.props.data.num_found_holo_chains > 0 && (
               <div className="found-struct-table scrollable-table">
                  <table id="holo-results">
                     <thead>
                        <tr>
                           <th data-tooltip="Matching structure (PDB_ID/chains)">Structure</th>
                           <th
                              data-tooltip={`Aligned to chain(s) of query structure (${this.props.data.target_pdb_id}). *query POI on hover.`}
                              className="at"
                           >
                              AT
                           </th>
                           {exp && (
                              <>
                                 <th
                                    data-tooltip="Resolution of the PDB structure (the best resolution is stated in the presence of multiple). Click to sort."
                                    style={this.sortableThStyle}
                                    onClick={(): void => this.handleSort('resolution')}
                                 >
                                    Res{this.sortIndicator('resolution')}
                                 </th>
                                 <th
                                    data-tooltip="Mapped binding residues (observed). Number of mapped binding residues in UniProt and SEQRES that are observed in the structure (#resis3 in results). *MBR-SEQRES on hover. Click to sort."
                                    className="so"
                                    style={this.sortableThStyle}
                                    onClick={(): void => this.handleSort('mapped_binding_residues_observed')}
                                 >
                                    MBR{this.sortIndicator('mapped_binding_residues_observed')}
                                 </th>
                                 <th
                                    data-tooltip="Disorder (unobserved residues). Number of mapped binding residues in UniProt and SEQRES that are not observed in the structure. Click to sort."
                                    style={this.sortableThStyle}
                                    onClick={(): void => this.handleSort('mapped_binding_residues_unobserved')}
                                 >
                                    Dis{this.sortIndicator('mapped_binding_residues_unobserved')}
                                 </th>
                                 <th
                                    data-tooltip="RMSD from the structural alignment (MM-align) of relevant chains (Ca atoms). Click to sort."
                                    style={this.sortableThStyle}
                                    onClick={(): void => this.handleSort('rmsd')}
                                 >
                                    RMSD{this.sortIndicator('rmsd')}
                                 </th>
                              </>
                           )}
                           <th data-tooltip="Ligands present in query pocket or mapped candidate pocket.">Lig</th>
                           <th
                              data-tooltip="Pocket RMSD: all-atom RMSD from the sequence-based pocket alignment (0 refinement cycles). Click to sort."
                              style={this.sortableThStyle}
                              onClick={(): void => this.handleSort('pocket_rmsd')}
                           >
                              pRMSD{this.sortIndicator('pocket_rmsd')}
                           </th>
                           <th
                              data-tooltip="Solvent Accessible Surface Area of pocket residues (UniProt SEQRES observed). Click to sort."
                              style={this.sortableThStyle}
                              onClick={(): void => this.handleSort('sasa')}
                           >
                              SASA{this.sortIndicator('sasa')}
                           </th>
                           {exp && (
                              <>
                                 <th
                                    data-tooltip="TM-score 1 from the structural alignment (MM-align) of the relevant candidate chains onto the relevant query chains. Click to sort."
                                    style={this.sortableThStyle}
                                    onClick={(): void => this.handleSort('tm_score')}
                                 >
                                    TM{this.sortIndicator('tm_score')}
                                 </th>
                                 <th data-tooltip="Pocket volume calculated by PyVOL (default settings).">Vol</th>
                              </>
                           )}
                           <th data-tooltip="Show/hide first 20 structures">
                              <button
                                 className="btn-showhideall"
                                 style={{ width: '1em' }}
                                 onClick={(): void => this.showHideAll(sortedData)}
                              >
                                 <img src="/icons/eye.svg" alt="show/hide" width="100%" />
                              </button>
                           </th>
                           <th data-tooltip="Edit representation">&#9998;</th>
                           <th data-tooltip="Add/remove from Sequence Viewer">Seq</th>
                           <th
                              data-tooltip={exp ? 'Collapse to default columns' : 'Expand to show all columns'}
                              style={{ cursor: 'pointer' }}
                              className={exp ? 'active' : ''}
                              onClick={(): void => this.toggleExpandedColumns()}
                           >
                              ℹ
                           </th>
                           <th data-tooltip="Quick color — click a row to apply a unique color pair">
                              {this.hasAnyColorPairActive(sortedData) ? (
                                 <button title="Reset all colors" onClick={(e): void => this.resetAllColors(e)}>
                                    &#8635;
                                 </button>
                              ) : (
                                 <span style={{ opacity: 0.35, fontSize: '0.85em' }}>◉</span>
                              )}
                           </th>
                        </tr>
                     </thead>
                     <tbody>
                        {sortedData.map((holo) => (
                           <React.Fragment key={holo.row_id}>
                              <tr
                                 data-row-id={`${holo.row_id}`}
                                 onClick={(): void => this.selectUnselectStructure(holo)}
                                 className={`clickable-row ${this.context.controlsMap.get(holo.row_id)?.isSelected ? 'selected-row' : ''}`}
                              >
                                 <td>
                                    <a
                                       href={`https://www.rcsb.org/structure/${holo.pdb_id}`}
                                       target="_blank"
                                       title="go to rcsb"
                                    >
                                       {holo.pdb_id}
                                    </a>
                                    &nbsp;<span className="slash">/</span>&nbsp;{render_chain_ids(holo.chains)}
                                 </td>
                                 <td title={holo.target_poi_alt}>{render_chain_ids(holo.target_chains)}</td>
                                 {exp && (
                                    <>
                                       <td title={`Exp. method: ${holo.experimental_method}`}>{holo.resolution}</td>
                                       <td
                                          title={`MBR-SEQRES: ${holo.mapped_binding_residues_num ?? 0} (${holo.mapped_binding_residues_percent ?? 0}%)`}
                                       >
                                          {holo.mapped_binding_residues_observed}
                                       </td>
                                       <td
                                          title={`% unobserved: ${holo.mapped_binding_residues_unobserved_percent ?? 0}%`}
                                       >
                                          {holo.mapped_binding_residues_unobserved ?? '–'}
                                       </td>
                                       <td title={`aligned length: ${holo.aln_len}`}>{holo.rmsd ?? '–'}</td>
                                    </>
                                 )}
                                 <td>{holo.ligands ? holo.ligands.join(' ') : '–'}</td>
                                 <td title={`aligned length: ${holo.pocket_len}`}>{holo.pocket_rmsd ?? '–'}</td>
                                 <td title={`avg. SASA: ${holo.sasa_avg ?? 0}`}>{holo.sasa ?? '–'}</td>
                                 {exp && (
                                    <>
                                       <td title={`inverse TM-score: ${holo.tm_score_inv ?? 0}`}>
                                          {holo.tm_score ?? '–'}
                                       </td>
                                       <td>{holo.volume ?? '–'}</td>
                                    </>
                                 )}
                                 <td>
                                    <button
                                       className={`btn-showhide${this.context.controlsMap.get(holo.row_id)?.isVisible ? '' : ' hidden'}`}
                                       style={{ width: '1em' }}
                                       title="show / hide"
                                       onClick={(e): void => {
                                          e.stopPropagation();
                                          this.showHideStructure(holo);
                                       }}
                                    >
                                       <img src="/icons/eye.svg" alt="show/hide" width="100%" />
                                    </button>
                                 </td>
                                 <td>
                                    <button
                                       title="edit"
                                       onClick={(e): void => this.toggleEditPanel(holo.row_id, e)}
                                       style={{ cursor: 'pointer' }}
                                    >
                                       {this.editingRowId === holo.row_id ? '▼' : '▶'}
                                    </button>
                                 </td>
                                 <td>
                                    <button
                                       title={
                                          this.context.selectedPdb.some((s) => s.rowId === holo.row_id)
                                             ? 'Remove from Sequence Viewer'
                                             : 'Add to Sequence Viewer'
                                       }
                                       className={`btn-seq${this.context.selectedPdb.some((s) => s.rowId === holo.row_id) ? ' active' : ''}`}
                                       onClick={(e): void => {
                                          e.stopPropagation();
                                          this.addOrRemoveFromRcsb(holo);
                                       }}
                                    >
                                       {this.context.selectedPdb.some((s) => s.rowId === holo.row_id) ? '−' : '+'}
                                    </button>
                                 </td>
                                 <td>
                                    <button
                                       className={`btn-info${this.infoRowId === holo.row_id ? ' active' : ''}`}
                                       title="Show info"
                                       onClick={(e): void => this.toggleInfoPanel(holo.row_id, e)}
                                    >
                                       ℹ
                                    </button>
                                 </td>
                                 <td className="color-pair-cell">
                                    <ColorPairButton
                                       pocketColor={
                                          this.context.controlsMap.get(holo.row_id)?.pocketColor ??
                                          getDefaultPocketColor('holo')
                                       }
                                       chainColor={
                                          this.context.controlsMap.get(holo.row_id)?.chainColor ??
                                          getDefaultChainColor('holo')
                                       }
                                       active={this.isColorPairActive(holo.row_id)}
                                       onClick={(e): void => {
                                          e.stopPropagation();
                                          this.selectNextColorPair(holo);
                                       }}
                                       title="Apply unique color pair"
                                    />
                                 </td>
                              </tr>
                              {this.editingRowId === holo.row_id && (
                                 <tr>
                                    <td colSpan={colSpan} className="edit-panel-cell">
                                       <div className="edit-panel-body">
                                          <div className="edit-panel-controls">
                                             <StructureRepresentationControlComponent
                                                mode="big"
                                                values={{
                                                   chain: {
                                                      representation:
                                                         this.context.controlsMap
                                                            .get(holo.row_id)
                                                            ?.selectedRepresentations?.get('chain') ??
                                                         defaultRestRepresentation,
                                                      color:
                                                         this.context.controlsMap.get(holo.row_id)?.chainColor ??
                                                         getDefaultChainColor('holo'),
                                                   },
                                                   pocket: {
                                                      representation:
                                                         this.context.controlsMap
                                                            .get(holo.row_id)
                                                            ?.selectedRepresentations?.get('pocket') ??
                                                         defaultPocketRepresentation,
                                                      color:
                                                         this.context.controlsMap.get(holo.row_id)?.pocketColor ??
                                                         getDefaultPocketColor('holo'),
                                                   },
                                                   ligand: {
                                                      representation:
                                                         this.context.controlsMap
                                                            .get(holo.row_id)
                                                            ?.selectedRepresentations?.get('ligand') ??
                                                         defaultLigandRepresentation,
                                                      color:
                                                         this.context.controlsMap.get(holo.row_id)?.ligandColor ??
                                                         getDefaultLigandColor(),
                                                   },
                                                }}
                                                onRepresentationChange={(
                                                   target: StructureComponentType,
                                                   type: Representation,
                                                ): void => this.changeRepresentation(type, holo, target)}
                                                onColorChange={(target: StructureComponentType, color: string): void =>
                                                   this.changeColor(holo, color, target)
                                                }
                                             />
                                          </div>
                                       </div>
                                    </td>
                                 </tr>
                              )}
                              {this.infoRowId === holo.row_id && (
                                 <tr>
                                    <td colSpan={colSpan} className="info-panel-cell">
                                       <div className="info-panel-body">
                                          <div className="info-panel-grid">
                                             <div className="info-panel-item">
                                                <span
                                                   className="info-panel-label"
                                                   data-tooltip="Resolution of the PDB structure (the best resolution is stated in the presence of multiple)."
                                                >
                                                   Res
                                                </span>
                                                <span
                                                   className="info-panel-value"
                                                   title={`Exp. method: ${holo.experimental_method}`}
                                                >
                                                   {holo.resolution ?? '–'}
                                                </span>
                                             </div>
                                             <div className="info-panel-item">
                                                <span
                                                   className="info-panel-label"
                                                   data-tooltip="Mapped binding residues (observed). Number of mapped binding residues in UniProt and SEQRES that are observed in the structure."
                                                >
                                                   MBR
                                                </span>
                                                <span
                                                   className="info-panel-value"
                                                   title={`MBR-SEQRES: ${holo.mapped_binding_residues_num ?? 0} (${holo.mapped_binding_residues_percent ?? 0}%)`}
                                                >
                                                   {holo.mapped_binding_residues_observed ?? '–'}
                                                </span>
                                             </div>
                                             <div className="info-panel-item">
                                                <span
                                                   className="info-panel-label"
                                                   data-tooltip="Disorder (unobserved residues). Number of mapped binding residues in UniProt and SEQRES that are not observed in the structure."
                                                >
                                                   Dis
                                                </span>
                                                <span
                                                   className="info-panel-value"
                                                   title={`% unobserved: ${holo.mapped_binding_residues_unobserved_percent ?? 0}%`}
                                                >
                                                   {holo.mapped_binding_residues_unobserved ?? '–'}
                                                </span>
                                             </div>
                                             <div className="info-panel-item">
                                                <span
                                                   className="info-panel-label"
                                                   data-tooltip="RMSD from the structural alignment (MM-align) of relevant chains (Ca atoms)."
                                                >
                                                   RMSD
                                                </span>
                                                <span
                                                   className="info-panel-value"
                                                   title={`aligned length: ${holo.aln_len}`}
                                                >
                                                   {holo.rmsd ?? '–'}
                                                </span>
                                             </div>
                                             <div className="info-panel-item">
                                                <span
                                                   className="info-panel-label"
                                                   data-tooltip="TM-score 1 from the structural alignment (MM-align) of the relevant candidate chains onto the relevant query chains."
                                                >
                                                   TM
                                                </span>
                                                <span
                                                   className="info-panel-value"
                                                   title={`inverse TM-score: ${holo.tm_score_inv ?? 0}`}
                                                >
                                                   {holo.tm_score ?? '–'}
                                                </span>
                                             </div>
                                             <div className="info-panel-item">
                                                <span
                                                   className="info-panel-label"
                                                   data-tooltip="Pocket volume calculated by PyVOL (default settings)."
                                                >
                                                   Vol
                                                </span>
                                                <span className="info-panel-value">{holo.volume ?? '–'}</span>
                                             </div>
                                          </div>
                                       </div>
                                    </td>
                                 </tr>
                              )}
                           </React.Fragment>
                        ))}
                     </tbody>
                  </table>
               </div>
            )}
         </div>
      );
   }
}

export default HoloSitesTable;
